import { useQuery } from "@tanstack/react-query";
import { abroadBrdApi, abroadLoansApi, abroadStudentApi } from "../../api/abroadLoans";
import { problemOf } from "../../api/scholarships";
import { openFile } from "../../utils/openFile";
import { ABROAD_KEY } from "./model";

// An approved abroad loan once the Minister signs it: the student's answer, travel, BRD's ticket and payments
export const AWARDS_KEY = [...ABROAD_KEY, "awards"];
export const myAwardKey = (id) => [...AWARDS_KEY, "mine", id];
const BRD_AWARDS_KEY = [...AWARDS_KEY, "brd"];

const ticketName = (id) => `ticket-${id}`;
const visaName = (id) => `visa-${id}`;

/**
 * An award (AwardView) in the shape the shared loan screens read (the scholarships API's): the
 * student's loan card and travel card, and BRD's pages. Payments BRD removed are left out.
 */
export const loanOf = (award, application) => ({
  applicationId: award.applicationId,
  applicant: { firstName: award.studentName },
  scholarship: { name: award.callName },
  programme: { name: award.programmeName, institutionName: award.institutionName, countryCode: award.countryCode },
  travels: true,
  approvedFunding: award.approvedAmounts,
  letter: { reference: award.letterReference, issuedAt: application?.decidedAt },
  response: award.accepted == null ? null : { accepted: award.accepted, at: award.respondedAt, reason: award.declineReason },
  payments: award.payments.filter((p) => !p.removedAt),
  tuitionPaid: award.tuitionPaid,
  allowancesSent: award.allowancesSent,
  travelSubmittedAt: award.travelSubmittedAt,
  ticketIssuedAt: award.ticket?.issuedAt ?? null,
  waitingForVisa: award.waitingForVisa,
  awaitingReply: award.awaitingReply,
  lastMessageAt: award.lastMessageAt,
  travel: {
    ...award.travel,
    submittedAt: award.travelSubmittedAt,
    visa: award.visa && { detectedType: award.visa.mediaType, sizeBytes: award.visa.sizeBytes },
    ticket: award.ticket,
  },
});

// The abroad API names a message's side and author flat
const toMessage = (m) => ({ ...m, author: { side: m.side, name: m.authorName } });

// The abroad awards in BRD's loan payments (see SCHOLARSHIP_LOANS in scholarships/brd/model.js);
// the approval letter is for HEC's staff and the student only
export const ABROAD_LOANS = {
  key: BRD_AWARDS_KEY,
  pathOf: (id) => `/loan-payments/abroad/${id}`,
  list: async () => (await abroadBrdApi.awards()).data.map((a) => loanOf(a)),
  one: async (id) => loanOf((await abroadBrdApi.award(id)).data),
  openLetter: null,
  openVisa: (id) => openFile(async () => (await abroadBrdApi.awardFile(id, "visa")).data, visaName(id)),
  openTicket: (id) => openFile(async () => (await abroadBrdApi.awardFile(id, "ticket")).data, ticketName(id)),
  uploadTicket: async (id, body) => ({ data: loanOf((await abroadBrdApi.issueTicket(id, body)).data) }),
  recordPayment: async (id, body) => ({ data: loanOf((await abroadBrdApi.recordPayment(id, body)).data) }),
  removePayment: (id, paymentId) => abroadBrdApi.removePayment(id, paymentId),
  messages: (id) => ({ path: `brd/awards/${id}/messages`, client: abroadLoansApi, toMessage, listKey: BRD_AWARDS_KEY }),
};

// The student's own award; none until the Minister approves
export const useMyAward = (id, approved) =>
  useQuery({
    queryKey: myAwardKey(id),
    enabled: Boolean(id) && approved,
    retry: (count, error) => problemOf(error)?.status !== 404 && count < 2,
    queryFn: async () => (await abroadStudentApi.award(id)).data,
  });

/** The student's calls on their award, for the shared loan and travel cards; `saved(award)` keeps each answer. */
export const studentAwardActions = (id, letterReference, saved) => {
  const run = async (call) => saved((await call()).data);
  return {
    respond: (body) => run(() => abroadStudentApi.answer(id, body)),
    openLetter: () => openFile(async () => (await abroadStudentApi.letter(id)).data, `approval-letter-${letterReference.replaceAll("/", "-")}.pdf`),
    save: (body) => run(() => abroadStudentApi.saveTravel(id, body)),
    submit: () => run(() => abroadStudentApi.submitTravel(id)),
    uploadVisa: (file) => run(() => abroadStudentApi.uploadVisa(id, file)),
    openVisa: () => openFile(async () => (await abroadStudentApi.awardFile(id, "visa")).data, visaName(id)),
    openTicket: () => openFile(async () => (await abroadStudentApi.awardFile(id, "ticket")).data, ticketName(id)),
  };
};

// The student's side of the BRD thread
export const studentThreadOf = (id) => ({ path: `applications/${id}/award/messages`, client: abroadLoansApi, toMessage, listKey: AWARDS_KEY });
